import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Star, GitFork } from 'lucide-react';
import projectsIcon from '../../assests/projects.png';

// Import Tech Logos
import langGraphImg from '../../assests/langGraph.png';
import dockerImg from '../../assests/docker.png';
import pytorchImg from '../../assests/pytorch.png';
import scilearnImg from '../../assests/sci-learn.png';

interface FeaturedProject {
  title: string;
  githubUrl: string;
  liveUrl?: string;
  description: string;
  impact: string;
  techniques: string[];
  techStack: { name: string; img?: string }[];
  color: string;
}

const featuredProjects: FeaturedProject[] = [
  {
    title: 'Vector Graph Hybrid Search',
    githubUrl: 'https://github.com/MDTOUFIQUE623/vector_graph_hybridsearch',
    liveUrl: 'https://graph-vector-rag.streamlit.app/',
    description: 'A sophisticated RAG application that ingests documents (PDF, DOCX, TXT, CSV, HTML), embeds them with sentence-transformers, stores vectors in ChromaDB, builds a knowledge graph in Memgraph, and runs hybrid retrieval combining semantic search with graph proximity.',
    impact: 'Mastered the integration of semantic search with structural knowledge graphs, drastically improving retrieval accuracy for complex multi-hop reasoning tasks. Gained deep experience in Dockerizing complex multi-database architectures.',
    techniques: [
      'Hybrid Retrieval Engine',
      'Entity Extraction (NER) via spaCy',
      'Vector Embeddings Generation',
      'Graph-DB proximity traversal'
    ],
    techStack: [
      { name: 'Streamlit' },
      { name: 'ChromaDB' },
      { name: 'Memgraph' },
      { name: 'spaCy' },
      { name: 'Docker', img: dockerImg }
    ],
    color: '#00B92E'
  },
  {
    title: 'AI Job Recommendation System',
    githubUrl: 'https://github.com/MDTOUFIQUE623/ai-job-recommendation',
    liveUrl: 'https://ai-job-recommender23.streamlit.app/',
    description: 'A modern AI-powered job recommender system that analyzes user resumes, identifies critical skill gaps, suggests a personalized career roadmap, and fetches real-time job and internship recommendations directly from LinkedIn and Naukri using Apify actors.',
    impact: 'Developed a robust understanding of building Agentic workflows and tool-calling systems using the Model Context Protocol (MCP). Learned to reliably scrape and parse dynamic data from major job boards.',
    techniques: [
      'Resume Parsing & Skill Gap Analysis',
      'Apify Web Scraping (LinkedIn/Naukri)',
      'Model Context Protocol (MCP) Tool Integration',
      'Prompt Engineering for Career Roadmaps'
    ],
    techStack: [
      { name: 'Python' },
      { name: 'Streamlit' },
      { name: 'Google GenAI (Gemini)' },
      { name: 'Apify' },
      { name: 'MCP Server' }
    ],
    color: '#3b82f6'
  },
  {
    title: 'MoodWave: Music Emotion Analyzer',
    githubUrl: 'https://github.com/MDTOUFIQUE623/music_emotion_analyzer',
    liveUrl: 'https://music-emotion-analyzer.onrender.com/',
    description: 'An advanced Music Emotion Recognition (MER) web application. Designed around high-performance machine learning, it extracts 70 distinct deep-acoustic features directly from raw audio waveforms to mathematically detect and map songs to intrinsic human emotions.',
    impact: 'Sharpened machine learning engineering skills by implementing 5-Fold Stratified Cross Validation to avoid dataset overfitting. Learned deep audio signal processing and built a seamless semantic bridge with OpenAI for rich artist recommendations.',
    techniques: [
      'Audio Signal Processing (70+ features including MFCCs)',
      'Voting Ensemble Classifiers (KNN, SVM RBF, RandomForest)',
      'OpenAI Contextual LLM bridging',
      'Jamendo API bulk data engineering'
    ],
    techStack: [
      { name: 'Python' },
      { name: 'Scikit-Learn', img: scilearnImg },
      { name: 'Librosa' },
      { name: 'Flask' },
      { name: 'OpenAI API' }
    ],
    color: '#f59e0b'
  }
];

interface Repo {
  id: number;
  name: string;
  description: string;
  html_url: string;
  stargazers_count: number;
  forks_count: number;
  language: string;
  topics: string[];
}

export default function Projects() {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('https://api.github.com/users/MDTOUFIQUE623/repos?sort=updated&per_page=12')
      .then((res) => res.json())
      .then((data) => {
        // Filter out the featured projects from the other repos list so we don't duplicate
        const featuredNames = ['vector_graph_hybridsearch', 'ai-job-recommendation', 'music_emotion_analyzer'];
        const otherRepos = data.filter((r: Repo) => !featuredNames.includes(r.name) && !r.name.toLowerCase().includes('portfolio'));
        setRepos(otherRepos);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch repos:', err);
        setLoading(false);
      });
  }, []);

  return (
    <section id="projects" className="py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-col mb-12"
        >
          <span className="text-white/50 uppercase tracking-[0.2em] text-sm mb-2">
            Featured Projects
          </span>
          <div className="flex items-center gap-4">
            <img src={projectsIcon} alt="" className="w-10 h-10 object-contain opacity-80" />
            <h2
              className="text-white font-bold section-label"
              style={{ fontSize: 'clamp(1rem, 5vw, 2.5rem)', letterSpacing: '0.05em', lineHeight: 1 }}
            >
              WORK
            </h2>
          </div>
        </motion.div>

        {/* Featured Projects List */}
        <div className="space-y-10 mb-12 lg:mb-24">
          {featuredProjects.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`group relative ${index > 0 ? 'hidden lg:block' : 'block'}`}
            >
              {/* Card Container - Reduced padding for smaller size */}
              <div className="relative z-10 bg-[#111111] border border-white/10 rounded-2xl p-6 lg:p-8 overflow-hidden transition-colors hover:bg-[#161616]">
                
                {/* Glow Effect */}
                <div 
                  className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-[100px] opacity-20 pointer-events-none transition-opacity group-hover:opacity-40"
                  style={{ backgroundColor: project.color }}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 relative z-10">
                  
                  {/* Left Column: Info */}
                  <div className="flex flex-col">
                    <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                      {project.title}
                    </h3>
                    
                    <p className="text-white/70 leading-relaxed mb-6 text-base">
                      {project.description}
                    </p>

                    <div className="flex gap-4 mb-6">
                      <a
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white hover:text-black transition-colors border border-white/20"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.2c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
                          <path d="M9 18c-4.51 2-5-2-7-2" />
                        </svg>
                        Source Code
                      </a>
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium hover:bg-white/10 transition-colors border border-white/20"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Live Demo
                        </a>
                      )}
                    </div>

                    {/* Tech Stack Pills */}
                    <div>
                      <h4 className="text-white/50 text-xs uppercase tracking-wider mb-3">Tech Stack</h4>
                      <div className="flex flex-wrap gap-2">
                        {project.techStack.map((tech) => (
                          <span
                            key={tech.name}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-white/80 bg-black/50 border border-white/10"
                          >
                            {tech.img && (
                              <img
                                src={tech.img}
                                alt={tech.name}
                                className="w-3.5 h-3.5 object-contain brightness-0 invert opacity-80"
                              />
                            )}
                            {tech.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Techniques & Impact */}
                  <div className="flex flex-col gap-6 bg-black/40 rounded-xl p-6 border border-white/5">
                    
                    {/* Techniques */}
                    <div>
                      <h4 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: project.color }} />
                        Techniques Mastered
                      </h4>
                      <ul className="space-y-2">
                        {project.techniques.map((tech, i) => (
                          <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
                            <span className="text-white/30">▹</span>
                            <span className="leading-relaxed">{tech}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Impact */}
                    <div>
                      <h4 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: project.color }} />
                        Development Impact
                      </h4>
                      <p className="text-white/70 text-sm leading-relaxed italic border-l-2 pl-3" style={{ borderColor: `${project.color}40` }}>
                        "{project.impact}"
                      </p>
                    </div>

                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Other Projects Section (Hidden on Mobile) */}
        <div className="hidden lg:block">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-8 border-t border-white/10 pt-16"
          >
            <h3 className="text-white font-bold text-2xl mb-2">Other Notable Projects</h3>
            <p className="text-white/50 text-sm">A collection of my other open-source experiments and tools.</p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-64 bg-white/5 rounded-xl animate-pulse border border-white/10" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {repos.map((repo, idx) => (
                <motion.a
                  key={repo.id}
                  href={repo.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="group block p-6 rounded-xl bg-white/[0.02] border border-white/10 hover:border-[#00B92E]/50 hover:bg-white/[0.04] transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-[#00B92E]">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    <div className="flex gap-3 text-white/40">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 group-hover:text-white/80 transition-colors">
                        <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.2c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
                        <path d="M9 18c-4.51 2-5-2-7-2" />
                      </svg>
                    </div>
                  </div>
                  
                  <h4 className="text-lg font-bold text-white mb-2 group-hover:text-[#00B92E] transition-colors truncate">
                    {repo.name}
                  </h4>
                  
                  <p className="text-sm text-white/60 line-clamp-3 mb-6 h-[60px]">
                    {repo.description || "No description provided."}
                  </p>

                  <div className="flex items-center justify-between text-xs text-white/40 mt-auto">
                    <div className="flex items-center gap-4">
                      {repo.language && (
                        <span className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#00B92E]/60" />
                          {repo.language}
                        </span>
                      )}
                      {repo.stargazers_count > 0 && (
                        <span className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5" />
                          {repo.stargazers_count}
                        </span>
                      )}
                      {repo.forks_count > 0 && (
                        <span className="flex items-center gap-1">
                          <GitFork className="w-3.5 h-3.5" />
                          {repo.forks_count}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          )}
        </div>

        {/* Mobile View More Github Button */}
        <div className="lg:hidden flex justify-center mt-6">
          <a
            href="https://github.com/MDTOUFIQUE623"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-8 py-3.5 rounded-full border-2 border-[#00B92E]/50 text-[#00B92E] font-bold tracking-widest text-sm hover:bg-[#00B92E] hover:text-black hover:border-[#00B92E] transition-all shadow-[0_0_15px_rgba(0,185,46,0.1)] hover:shadow-[0_0_20px_rgba(0,185,46,0.4)]"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.2c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
              <path d="M9 18c-4.51 2-5-2-7-2" />
            </svg>
            VIEW MORE ON GITHUB
          </a>
        </div>
      </div>
    </section>
  );
}
